import { store } from 'store';
import dayjs from 'dayjs';

/**
 * ערכים דינמיים יחסיים שניתן להשתמש בהם בפילטרים
 */
export type DynamicValue =
  | 'current_user' // משתמש נוכחי
  | 'today' // היום
  | 'yesterday' // אתמול
  | 'this_week' // השבוע הנוכחי
  | 'last_week' // השבוע שעבר
  | 'this_month' // החודש הנוכחי
  | 'last_month' // החודש שעבר
  | `last_${number}_minutes` // X הדקות האחרונות (כמות+יחידה)
  | `last_${number}_hours` // X השעות האחרונות (כמות+יחידה)
  | `last_${number}_days`; // X הימים האחרונים (כולל ה-presets הישנים 7/30/90)

/** ערך יחסי "ב-X האחרונים" — כמות + יחידה. ימים נפתרים מתחילת היום (תאימות ל-presets הישנים). */
export const RELATIVE_LAST_RE = /^last_(\d+)_(minutes|hours|days)$/;

export type RelativeLastParts = { amount: number; unit: 'minutes' | 'hours' | 'days' };

/** מפרק ערך דינמי יחסי (עם או בלי קידומת __dynamic__) לכמות ויחידה; null אם אינו כזה */
export function parseRelativeLastDynamic(value: unknown): RelativeLastParts | null {
  if (typeof value !== 'string') return null;
  const key = value.startsWith('__dynamic__')
    ? value.slice('__dynamic__'.length)
    : value.startsWith('_dynamic_')
      ? value.slice('_dynamic_'.length)
      : value;
  const match = RELATIVE_LAST_RE.exec(key);
  if (!match) return null;
  const amount = Number(match[1]);
  if (!Number.isInteger(amount) || amount < 1) return null;
  return { amount, unit: match[2] as RelativeLastParts['unit'] };
}

/**
 * ממיר ערך דינמי לערך אמיתי
 * @param value הערך הדינמי או ערך רגיל
 * @param fieldType סוג השדה ('dateTime' | 'manager' | 'other')
 * @returns הערך האמיתי
 */
export function resolveDynamicValue(value: any, fieldType?: 'dateTime' | 'manager' | 'other'): any {
  // אם זה לא ערך דינמי, החזר כמו שהוא
  if (typeof value !== 'string' || (!value.startsWith('__dynamic__') && !value.startsWith('_dynamic_'))) {
    return value;
  }

  // חלץ את הערך הדינמי - תמוך בשני פורמטים: __dynamic__ ו-_dynamic_
  const dynamicValue = (value.startsWith('__dynamic__') 
    ? value.replace('__dynamic__', '') 
    : value.replace('_dynamic_', '')) as DynamicValue;

  // טיפול בערכים דינמיים לפי סוג השדה
  if (fieldType === 'manager') {
    if (dynamicValue === 'current_user') {
      const currentUser = store.getState().user;
      return currentUser?.Udid || null;
    }
  }

  if (fieldType === 'dateTime') {
    const now = dayjs();

    // "ב-X האחרונים" גנרי (כמות+יחידה) — מכסה גם את ה-presets הישנים last_7/30/90_days.
    // דקות/שעות נפתרות מדויק מהרגע הנוכחי; ימים מתחילת היום (כמו ההתנהגות ההיסטורית).
    const relative = parseRelativeLastDynamic(dynamicValue);
    if (relative) {
      if (relative.unit === 'days') {
        return now.subtract(relative.amount, 'day').startOf('day').toISOString();
      }
      return now.subtract(relative.amount, relative.unit === 'hours' ? 'hour' : 'minute').toISOString();
    }

    switch (dynamicValue) {
      case 'today':
        return now.startOf('day').toISOString();
      case 'yesterday':
        return now.subtract(1, 'day').startOf('day').toISOString();
      case 'this_week':
        return now.startOf('week').toISOString();
      case 'last_week':
        return now.subtract(1, 'week').startOf('week').toISOString();
      case 'this_month':
        return now.startOf('month').toISOString();
      case 'last_month':
        return now.subtract(1, 'month').startOf('month').toISOString();
      default:
        return value;
    }
  }

  return value;
}

/**
 * יוצר ערך דינמי מתוך מפתח דינמי
 */
export function createDynamicValue(dynamicValue: DynamicValue): string {
  return `__dynamic__${dynamicValue}`;
}

/**
 * בודק אם ערך הוא ערך דינמי
 */
export function isDynamicValue(value: any): boolean {
  return typeof value === 'string' && value.startsWith('__dynamic__');
}

